
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class ReliableComponentBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      retryCount: 0
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`${this.props.componentName || 'Component'} error:`, error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="win95-container p-4 border border-black">
          <div className="flex items-center mb-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-lg font-bold">Component Loading Error</h3>
          </div>
          
          <p className="text-sm text-gray-700 mb-3">
            {this.props.componentName || 'This component'} failed to load properly.
          </p>
          
          {this.state.retryCount < 3 && (
            <Button 
              onClick={this.handleRetry}
              size="sm"
              className="flex items-center"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again ({this.state.retryCount + 1}/3)
            </Button>
          )}
          
          {this.state.retryCount >= 3 && (
            <p className="text-xs text-gray-500 mt-2">
              Multiple retry attempts failed. Please refresh the page.
            </p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
